import { Assignment } from '../../api/app-types/assignment';
import { DB_PHASES } from '../../api/app-types/phase.app';
import { RoleId } from '../../api/app-types/role.app';

type UserId = string | number | null | undefined;

export interface FdsReportAccessInput {
	assignment: Assignment | undefined;
	relatedAssignments: Assignment[];
	effectiveUserId: UserId;
	hasReadPermission: boolean;
	hasEditPermission: boolean;
	hasOwnerLikeAccess: boolean;
	roleId?: string;
}

const QUALIFYING_ACTIVE_PHASES = new Set<number>([DB_PHASES.P2, DB_PHASES.P3, DB_PHASES.FP3]);
const ALLOWED_REPORT_TARGET_PHASES = new Set<number>([DB_PHASES.P4, DB_PHASES.FP4, DB_PHASES.FP5]);
const FDS_DRAFT_READER_ROLES = new Set<string>([RoleId.IFD_TEAM_LEADER, RoleId.IFD_COORDINATION, RoleId.ADMIN]);

export function flattenAssignments(assignments: Assignment[]): Assignment[] {
	return assignments.flatMap((assignment) => [assignment, ...(assignment.additionalAssignments ?? [])]);
}

export function hasQualifyingCaseAccess(
	assignment: Assignment | undefined,
	relatedAssignments: Assignment[],
	effectiveUserId: UserId,
): boolean {
	if (!assignment || effectiveUserId == null) return false;

	return flattenAssignments(relatedAssignments).some(
		(candidate) =>
			String(candidate.clientId) === String(assignment.clientId) &&
			QUALIFYING_ACTIVE_PHASES.has(candidate.phaseId) &&
			String(candidate.createdByUser?.id) === String(effectiveUserId),
	);
}

export function canAccessFdsReport(input: FdsReportAccessInput): boolean {
	const {
		assignment,
		relatedAssignments,
		effectiveUserId,
		hasReadPermission,
		hasEditPermission,
		hasOwnerLikeAccess,
		roleId,
	} = input;
	if (!assignment) return false;
	if (!hasReadPermission && !hasEditPermission) return false;

	// Approval requires reading FP3 drafts of already-visible FDS assignments, not additional write access.
	if (
		assignment.isServiceOfficeStatement &&
		assignment.phaseId === DB_PHASES.FP3 &&
		hasReadPermission &&
		roleId != null &&
		FDS_DRAFT_READER_ROLES.has(roleId)
	) {
		return true;
	}

	// Own/represented own assignments: no phase restriction
	if (hasOwnerLikeAccess) {
		return hasEditPermission || hasReadPermission;
	}

	// Cross-case access (via qualifying active assignment): only for P4/FP4/FP5
	if (!ALLOWED_REPORT_TARGET_PHASES.has(assignment.phaseId)) return false;
	if (!hasEditPermission) return true;

	return hasQualifyingCaseAccess(assignment, relatedAssignments, effectiveUserId);
}
